import React, { useMemo, useState } from 'react';

import {
  OrganizationOnboardingType,
  OrganizationPublisherOnboardingType,
} from '@ts/userOrganization.types';
import { Button } from '@uikit/Form/Button/Button';
import Input from '@uikit/Form/Input/Input';

type Props = {
  details?: OrganizationOnboardingType;
  onSave: (details: Partial<OrganizationPublisherOnboardingType>, isCompleted: boolean) => void;
  onBack?: () => void;
  onContinue?: () => void;
};

function OnboardingPublisherLinkedIn({ details, onSave, onBack, onContinue }: Props): JSX.Element {
  const initial = useMemo(() => details?.publisher?.linkedIn || '', [details]);
  const [value, setValue] = useState<string>(initial);
  const [touched, setTouched] = useState<boolean>(false);
  const [errorText, setErrorText] = useState<string | undefined>(undefined);

  const validate = (val: string) => {
    const trimmed = val.trim();

    // Empty is allowed
    if (!trimmed) {
      return undefined;
    }

    // Must be a LinkedIn URL
    const linkedinRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/.*$/i;
    if (!linkedinRegex.test(trimmed)) {
      return 'Please enter a valid LinkedIn URL';
    }

    // Accept personal profiles or company pages
    const personalRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/in\/[^\/?#]+\/?$/i;
    const companyRegex = /^(https?:\/\/)?(www\.)?linkedin\.com\/company\/[^\/?#]+(\/.*)?$/i;

    if (!personalRegex.test(trimmed) && !companyRegex.test(trimmed)) {
      return 'Please enter a personal or company LinkedIn URL';
    }

    return undefined;
  };

  const currentError = useMemo(() => validate(value), [value]);
  const isValid = !currentError;

  const handleContinue = () => {
    if (!isValid) {
      setTouched(true);
      setErrorText(currentError);
      return;
    }
    onSave({ linkedIn: value }, false);
    onContinue && onContinue();
  };

  return (
    <div className="onboarding-step onboarding-step__three">
      <h2 className="onboarding-step__title">What's your Linkedin profile?</h2>
      <div className="onboarding-step__content">
        <div className="onboarding-step__input">
          <Input
            type="url"
            placeholder="Enter your Linkedin profile URL"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setErrorText(undefined);
            }}
            onBlur={() => {
              setTouched(true);
              setErrorText(currentError);
            }}
            isError={touched && !!errorText}
            error={touched ? errorText : undefined}
          />
        </div>
      </div>
      <div className="onboarding-step__footer">
        <Button type="secondary" onClick={onBack}>
          Back
        </Button>
        <Button onClick={handleContinue} disabled={!isValid}>
          Continue
        </Button>
      </div>
    </div>
  );
}

export default OnboardingPublisherLinkedIn;
